import { ProgressUpdate } from '../../../blocks/ProgressUpdateCard/types';
import { GetTicketCheckInsResponse } from '../types/queryTypes';
import {
  CheckInData,
  CheckInTypeEnum,
  TAdhockCheckIn,
  TicketOption,
  TTicketCheckIn,
  TValidationResult,
} from '../types/types';

export const validateForm = (formState: CheckInData): TValidationResult => {
  if (formState.type === CheckInTypeEnum.AD_HOC) {
    if (!formState.title) {
      return {
        isValid: false,
        errorMessage: 'Please provide a title',
      };
    }
  }

  if (formState.type === CheckInTypeEnum.TICKET) {
    if (!formState.ticketId) {
      return {
        isValid: false,
        errorMessage: 'Please select a ticket',
      };
    }
  }

  if (!formState.description?.trim()) {
    return {
      isValid: false,
      errorMessage: 'Please provide a description',
    };
  }

  if (formState.hasBlockers && !formState.blockers?.trim()) {
    return {
      isValid: false,
      errorMessage: 'Please describe your blockers',
    };
  }

  return { isValid: true };
};

export const createPayload = (
  formState: CheckInData,
  userId: string,
  checkInId: string | null,
  selectedTab: number,
  tickets?: TicketOption[],
  title?: string,
): TTicketCheckIn | TAdhockCheckIn => {
  const basePayload = {
    userId,
    checkInType: formState.type,
    checkInId,
    dailySequence: selectedTab + 1,
    description: formState.description,
    ...(formState.id && { id: formState.id }),
  };

  return formState.type === CheckInTypeEnum.AD_HOC
    ? {
        ...basePayload,
        title: formState.title,
      }
    : {
        ...basePayload,
        ticketId: formState.ticketId,
        blockers:
          formState.hasBlockers && formState.blockers ? formState.blockers : '',
        wipLinks: formState.wipMaterials,
        completionPercentage: formState.progress,
        title:
          title || tickets?.find((t) => t.value === formState.ticketId)?.label,
        previousCompletionPercentage: formState.prevProgress,
      };
};

export const isTicketUpdate = (update: { type: string }): boolean => {
  return update.type === CheckInTypeEnum.TICKET;
};

export const transformUpdate = (update: ProgressUpdate): CheckInData => {
  return {
    ...update,
    type:
      update.type === 'adhoc_task'
        ? CheckInTypeEnum.AD_HOC
        : CheckInTypeEnum.TICKET,
    hasBlockers: update.type === CheckInTypeEnum.TICKET && !!update.blockers,
    wipMaterials:
      update.type === CheckInTypeEnum.TICKET ? (update.wipLinks as string) : '',
    prevProgress:
      update.type === CheckInTypeEnum.TICKET
        ? update.progress?.previous || 0
        : 0,
    progress:
      update.type === CheckInTypeEnum.TICKET
        ? update.progress?.current || 0
        : 0,
    blockers:
      update.type === CheckInTypeEnum.TICKET ? (update.blockers as string) : '',
    ...(update.type === CheckInTypeEnum.TICKET && {
      ticketId: update.ticketId || '',
    }),
  };
};

export const transformCheckIn = (
  checkIn: GetTicketCheckInsResponse,
): ProgressUpdate => {
  if (checkIn.check_in_type === 'adhoc_task') {
    return {
      id: checkIn.id,
      checkInId: checkIn.check_in_id,
      type: 'adhoc_task',
      title: checkIn.title,
      description: checkIn.description,
    };
  }

  return {
    id: checkIn.id,
    checkInId: checkIn.check_in_id,
    type: 'ticket',
    ticketId: checkIn.ticket_id,
    title: checkIn.title || '',
    description: checkIn.description,
    progress: {
      current: checkIn.completion_percentage || 0,
      previous: checkIn.previous_completion_percentage || 0,
    },
    blockers: checkIn.blockers,
    wipLinks: checkIn.wip_links,
    externalLink: checkIn.ticket.external_link,
  };
};

export const renderLinks = (content: string): (string | JSX.Element)[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return content.split(urlRegex).map((part, index) =>
    urlRegex.test(part) ? (
      <a
        // eslint-disable-next-line react/no-array-index-key
        key={`${part}-${index}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline break-all"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
};
